import type { Price, SourceableRef } from '@misumi/shared'
import type { components } from '../../lib/api/schema.gen'
import type { OrderStatus, Shipment } from '../orders/domain'
import type { WorkspaceSummary } from '../workspaces/types'

/**
 * WS-3 Parts Library (UID-44, docs/design/screens.md §5) — typed shapes for
 * the `/parts` screen's two tabs (My parts / BOMs).
 *
 * These mirror the FROZEN BFF contract the software-engineer handed off for
 * `GET /api/parts` (ADR-0030's Sourceable projection boundary) — field
 * names, nesting, and the `all|ready|config|quote|match` / `recent|name`
 * query-param vocabularies are copied verbatim so the eventual swap from
 * `mockPartsLibraryService` to a real `apiClient`-backed service is a
 * signature-compatible drop-in, not a reshape.
 *
 * `LibrarySourceable`'s extension fields (`dfmSummary`, `pricingProjection`,
 * `libraryStatus`, etc.) aren't in `packages/shared` yet — defined locally
 * here as a mirror of the handoff. Replace this block with a
 * `@misumi/shared` import once the software-engineer's schema addition
 * lands on `main`; `SourceableRef` (id/kind/accountId/uom) already comes
 * from there today.
 */

export type SourceableProvenance = 'misumi_standard' | 'partner' | 'fictiv_custom'

/** The Sourceable's own intake/pricing lifecycle stage — distinct from
 * `LibraryPartRow.filterStatus`, which is the *screen's* derived filter
 * bucket (see components.md "Price display"'s 3-state pricing card). */
export type LibraryStatus = 'catalog' | 'analyzing' | 'analyzed' | 'quote_required' | 'quoted'

/**
 * UID-151 (E1): `pass` is unused today — the generated contract
 * (`schema.gen.ts` `DfmSummary.findings`) is `string[]`, so every finding
 * that reaches the UI is FE-derived to `'info'` (see `toDfmFindings` in
 * `./dfm.ts`). `pass`/`flag` are kept in the union, not deleted, so a
 * richer per-finding severity (Story E0, deferred) is a additive contract
 * change, not a view-model reshape.
 */
export type DfmFindingSeverity = 'pass' | 'flag' | 'info'

/**
 * FE render shape for one discrepancy row (components.md discrepancy row:
 * severity + message + detail). NOT the wire shape — the generated
 * `DfmSummary.findings` is `string[]` (UID-49; no `DfmFinding` schema
 * exists yet, see `docs/DfmSummary` note in Story E0). Every `DfmFinding`
 * rendered today is produced by `toDfmFindings` in `./dfm.ts`, which maps a
 * finding string 1:1 to `{ severity: 'info', label }` — never fabricates a
 * `pass`/`flag` severity the contract doesn't carry.
 */
export interface DfmFinding {
  readonly severity: DfmFindingSeverity
  readonly label: string
  readonly detail?: string
}

/**
 * Mirrors the generated `components["schemas"]["DfmSummary"]`
 * (`schema.gen.ts`): `findings` is `string[]` on the wire today. Render
 * code maps strings to `DfmFinding[]` via `toDfmFindings` at the view
 * boundary rather than storing the FE shape on this contract mirror.
 */
export interface DfmSummary {
  readonly manufacturable: boolean
  readonly findings?: readonly string[]
  readonly suggestedProcess?: string
  readonly suggestedMaterial?: string
  readonly indicativeLeadTimeDays?: number
}

/** Drives the Price display primitive's 3 mutually-exclusive states
 * (components.md "Price display": auto-price curve / quote-required /
 * locked-to-RFQ). */
export type PricingProjectionMode = 'auto_priced' | 'quote_required' | 'locked_to_rfq'

/** One rung of the volume-pricing curve — reused verbatim from the generated
 * contract (the SAME schema `CartLinePresentation.volumeTiers` uses, ADR-0058
 * §1). No second tier type is defined here. */
export type VolumePricingTier = components['schemas']['VolumePricingTier']

/** The advisory quantity-break curve for an `auto_priced` Sourceable
 * (UID-159, ADR-0058). Reused verbatim from the generated contract — see
 * `schema.gen.ts`'s `VolumePricingCurve` doc comment for the full ADR
 * rationale (advisory-only, no order-eligible price, above-`cap` → RFQ). */
export type VolumePricingCurve = components['schemas']['VolumePricingCurve']

export interface PricingProjection {
  readonly mode: PricingProjectionMode
  readonly lockedRfqId?: string
  readonly lockedQuoteId?: string
  /** Present only for `mode: 'auto_priced'` (ADR-0058) — absent for
   * `quote_required` / `locked_to_rfq`, and absent for an `auto_priced` part
   * whose account has no configured `volumeCap` (A1). */
  readonly volumeCurve?: VolumePricingCurve
}

export type LibrarySourceable = SourceableRef & {
  readonly name?: string
  readonly customerBomId?: string
  readonly catalogSku?: string
  /** Name→value config bag (`configurable`/`custom` kinds). Widened to
   * `unknown` values (UID-163/C3) to match the REAL generated contract
   * (`schema.gen.ts`: `{ [key: string]: unknown }`) — process/material/
   * finish are strings, but quantity/threadedHoleCount are numbers and
   * inspections/certificates are string arrays (C0's `EditPartConfigRequest`,
   * `openapi.yaml:1666`), so a `string`-only value type was already a lie
   * about the wire shape, just one nothing had read yet. */
  readonly configAttributes?: Readonly<Record<string, unknown>>
  readonly indicativePrice?: Price
  readonly provenance?: SourceableProvenance
  readonly libraryStatus?: LibraryStatus
  readonly dfmSummary?: DfmSummary
  readonly pricingProjection?: PricingProjection
}

/** The screen's All/Ready/Config/Quote/Match filter chips — `'all'` is
 * UI-only (never a row's `filterStatus`); the other four are exactly the
 * BFF's `?status=` query-param vocabulary and `LibraryPartRow.filterStatus`
 * values, so no client-side translation table is needed. */
export const PART_STATUS_FILTERS = ['all', 'ready', 'config', 'quote', 'match'] as const
export type PartStatusFilter = (typeof PART_STATUS_FILTERS)[number]
export type PartFilterStatus = Exclude<PartStatusFilter, 'all'>

export const PART_STATUS_FILTER_LABEL: Record<PartStatusFilter, string> = {
  all: 'All',
  ready: 'Ready',
  config: 'Config',
  quote: 'Quote',
  match: 'Match',
}

export type PartsSortKey = 'recent' | 'name'

/**
 * UID-170/F2 (Story F0's `LibraryRowStatus`, `schema.gen.ts`): the EXPLICIT
 * per-row lifecycle state the readiness-driven listing renders its
 * primary-action cell from — read verbatim from the generated contract
 * (not hand-typed) rather than forked. See `schema.gen.ts`'s doc comment
 * for the full rationale: complements (does not replace) `filterStatus`,
 * and adds the transient `analyzing` state the filter chips never expressed.
 */
export type LibraryRowStatus = components['schemas']['LibraryRowStatus']

export interface LibraryPartRow {
  readonly sourceable: LibrarySourceable
  readonly thumbnailUrl: string | null
  readonly drawingFilename: string | null
  readonly requirements: string
  readonly quantity: number
  readonly inRfq: boolean
  readonly filterStatus: PartFilterStatus
  /** UID-170/F2: optional because it's a new, additive field (F0/F1) — a
   * row from an older fixture / not-yet-upgraded BFF response simply omits
   * it. `resolveRowStatus` (PartsTable.tsx) derives an equivalent state from
   * `filterStatus` when this is absent, so every row still renders a
   * primary action even before every caller sends this field. */
  readonly rowStatus?: LibraryRowStatus
  readonly sessionId: string | null
  /** UID-225 — the human-friendly upload-session label the grouped ("Group by
   * session") view renders as its group heading (e.g. "Linear stage — rev 1").
   * NEVER the raw `sessionId`: the internal grouping id must not leak into the
   * UI. `null` when the row belongs to no upload session. */
  readonly sessionName: string | null
  /** UID-225 — the upload session's created/uploaded timestamp, for the grouped
   * view's meta line ("… · uploaded May 30"). `null` when there is no session. */
  readonly sessionCreatedAt: string | null
  readonly updatedAt: string
  /** UID-297 (ADR-0107) — the projects (workspaces) this part is linked to,
   * server read-back of the "Add to project" link (durable — survives a
   * refresh, unlike the old FE-only accumulated map). Absent for a part
   * linked to no project the caller can see. */
  readonly projects?: readonly WorkspaceSummary[]
  /** UID-405 (design-parity audit fix) — RFQ-bundle lock (design
   * `Fictiv Platform.dc.html:8611,8618,8624-8627`'s `p.rfqLock`): when
   * present, this part belongs to a bundled/multi-part RFQ and must be
   * purchased together with the rest of that quote — its primary action
   * becomes the disabled "Bundled" chip (`resolveRowStatus` ->
   * `'bundled'`) regardless of the row's other state.
   *
   * CONTRACT GAP, FLAGGED: `GET /api/parts` has no field for this today.
   * Added here as the minimal, additive, OPTIONAL FE view-model field
   * (same posture as `rowStatus`/`projects` above) so the "Bundled" CTA
   * state is demonstrable in the UI ahead of the real backend field
   * landing — not a fabricated value; every real row simply omits it
   * (falls through to its normal resolved status) until the software
   * engineer adds server support. */
  readonly rfqLock?: { readonly rfqId: string } | null
  /** UID-405 (design-parity audit fix) — server-derived expiry flag
   * (design `Fictiv Platform.dc.html:8611`'s `isQuoteExpired(p)`): true
   * when a `ready` row's underlying quote has passed its validity window,
   * so the primary action must read "Request quote" again
   * (`resolveRowStatus` -> `'quote_expired'`) rather than "Add to cart".
   * SERVER-DERIVED like `PartDetailQuotedPricing.expired` above (types.ts)
   * — never inferred client-side against the wall clock.
   *
   * CONTRACT GAP, FLAGGED: same posture as `rfqLock` immediately above —
   * no backend field exists yet on `GET /api/parts`; added as the minimal
   * additive field needed to demonstrate this CTA state, not a fabricated
   * client-side date computation. */
  readonly quoteExpired?: boolean
  /** UID-577 — the catalog/stock availability line the REQUIREMENTS cell shows
   * BELOW a Buy row's configuration (design `p.lead` +
   * `reqSubArr`/`reqSubStyle`, `Fictiv Platform.dc.html:8640-8644`): a free
   * string like `"In stock"` or `"Ships in 5 days"`. The design renders an
   * in-stock value in teal/semibold and appends the orderable ceiling
   * ("In stock · up to 500 available"); see `formatStockAvailability`
   * (format.ts) for the exact mapping.
   *
   * Meaningful only for a standard/catalog row — a custom (Make) part's
   * sub-line is its material/tolerance/finish instead, per the design's own
   * `isCustom` branch.
   *
   * CONTRACT GAP, FLAGGED: `GET /api/parts` has no lead-time or stock field
   * today, so this is the minimal, additive, OPTIONAL view-model field needed
   * to render the design's state — same posture as `rfqLock`/`quoteExpired`
   * above, and NOT a fabricated client-side inference. A row that omits it
   * simply renders no sub-line (the pre-UID-577 behavior), so nothing invents
   * an availability claim the server never made. */
  readonly leadTime?: string | null
}

export type BomLibraryStatus = 'draft' | 'in_review' | 'approved' | 'ordered'

export const BOM_STATUS_LABEL: Record<BomLibraryStatus, string> = {
  draft: 'Draft',
  in_review: 'In review',
  approved: 'Approved',
  ordered: 'Ordered',
}

export interface LibraryBomRow {
  readonly id: string
  readonly name: string
  readonly status: BomLibraryStatus
  readonly partCount: number
  readonly estimatedCost: Price | null
  readonly maxLeadTimeDays: number | null
  readonly sessionId: string | null
  readonly updatedAt: string
  /** UID-295/296 — free-form BOM-level description shown in the card-grid preview.
   * `null`/absent when the BOM has none. */
  readonly description?: string | null
  /** UID-299 follow-up (`Project Workspace.dc.html:172`) — the BOM card's row of
   * constituent-part thumbnails, capped at 3 server-side. Omitted when no
   * constituent part has a thumbnail yet. */
  readonly thumbnailUrls?: readonly string[]
}

/** `GET /api/parts` response shape. `counts` are FULL-library totals for
 * the tab badges — NOT filtered by the query below.
 *
 * UID-603 (ADR-0176 D1.3) — `nextCursor`/`totalCount` are ADDITIVE and
 * OPTIONAL: they describe the `parts` collection only (`boms` is never
 * paginated, D7.1), and their ABSENCE means "this response was not
 * paginated" (no `limit` was sent) rather than a sentinel value. `totalCount`
 * is the count matching the CURRENTLY APPLIED FILTER (`q`/`status`) — do NOT
 * confuse it with `counts.parts`, which stays the unfiltered full-library
 * total the tab badge reads. */
export interface LibraryListing {
  readonly parts: readonly LibraryPartRow[]
  readonly boms: readonly LibraryBomRow[]
  readonly counts: {
    readonly parts: number
    readonly boms: number
  }
  readonly nextCursor?: string | null
  readonly totalCount?: number
}

/** `GET /api/parts` query params (`?q=&status=&sort=&groupBySession=`). The
 * BFF owns filtering/sorting — the mock service applies the same query
 * against fixture data so the screen never re-implements this logic
 * against a real API response. */
export interface LibraryListingQuery {
  readonly q?: string
  readonly status?: PartStatusFilter
  readonly sort?: PartsSortKey
  readonly groupBySession?: boolean
  /** UID-297 — the toolbar "All projects" filter, applied server-side. */
  readonly workspaceId?: string
  /** UID-603 (ADR-0176 D1.2) — optional; omitting BOTH `limit` and `cursor`
   * preserves the pre-UID-603 unbounded response. Only `usePartsLibrary`
   * passes these — `sku-resolver.ts`/`add-parts-search.ts`/`bom-store`-style
   * whole-library callers must keep omitting them (D6/D7.3). */
  readonly limit?: number
  readonly cursor?: string
}

export type PartsLibraryTab = 'parts' | 'boms'

/**
 * UID-45 Part detail (screens.md §6) — `GET /api/parts/:partId` contract
 * handoff: every `LibraryPartRow` field PLUS purchase history + an active-
 * quote count. The "Part activity" tab's timeline (UID-236) is carried on
 * `PartDetail.activity`, normalized from the contract's `activityLog`.
 */
export interface PartRevisionOrder {
  readonly revision: string
  readonly orderId: string
  readonly orderedOn: string
  readonly orderedBy: string
  readonly quantity: number
  readonly unitPrice: Price | null
  /** UID-332 (AC-B2, ADR-0113) — additive rich per-order fields for the nested
   * revision→orders card. Absent for a pre-UID-332 / fixture order row. */
  readonly name?: string
  readonly status?: string
  readonly leadTimeDays?: number | null
  readonly requirements?: string
  /** Not `readonly` (unlike this interface's other array-ish fields): the
   * element type is the GENERATED `PartRevisionFile` (S3 code-review finding
   * — no longer hand-mirrored), and `PartRevision.orders` from the same
   * generated contract types its nested `files` as a mutable array, so this
   * field must stay structurally assignable to that generated shape rather
   * than adding a readonly promise the wire type doesn't make. */
  readonly files?: PartRevisionFile[]
}

/** UID-332 (AC-B2, ADR-0113) — one downloadable/openable file chip on a
 * revision or an order row (drawing or 3D model). Read verbatim from the
 * generated contract (S3 code-review finding: hand-mirrored interfaces drift
 * from `schema.gen.ts` over time — see `QuoteState` below for the same
 * precedent this follows). */
export type PartRevisionFile = components['schemas']['PartRevisionFile']

/** UID-332 (ADR-0113) — an inclusive low→high band of the shared `Price`
 * value object, used for a quoted-price range. Read verbatim from the
 * generated contract (S3). */
export type PriceRange = components['schemas']['PriceRange']

/** UID-332 (AC-B4, ADR-0113) — one linkable RFQ chip in the part-detail header,
 * the multi-RFQ replacement for the boolean `inRfq`. Read verbatim from the
 * generated contract (S3). */
export type PartRfqChip = components['schemas']['PartRfqChip']

/** UID-332 (AC-B1, ADR-0113) — the QUOTED-state pricing card projection.
 * `expired` is SERVER-DERIVED (never inferred client-side against the wall
 * clock). Present ONLY when there is a quote to show — its presence is the
 * `isQuote` signal for the pricing card. Read verbatim from the generated
 * contract (S3). */
export type PartDetailQuotedPricing = components['schemas']['PartDetailQuotedPricing']

/** UID-332 (AC-B3, ADR-0113) — the canonical Quote lifecycle vocabulary the
 * active-quote badge reuses. Read verbatim from the generated contract. */
export type QuoteState = components['schemas']['QuoteState']

/** UID-332 (AC-B3, ADR-0113) — one row on the part-detail "Active quotes" tab, a
 * slim projection off the shared Quote aggregate. Read verbatim from the
 * generated contract (S3). */
export type PartActiveQuote = components['schemas']['PartActiveQuote']

/** UID-332 (AC-B2, ADR-0113) — one revision card nesting its purchased orders,
 * the additive nested companion to the flat `revisions`. `superseded` is
 * server-derived. Read verbatim from the generated contract (S3). */
export type PartRevision = components['schemas']['PartRevision']

export interface PartDetail extends LibraryPartRow {
  readonly revisions: readonly PartRevisionOrder[]
  readonly activeQuoteCount: number
  /** UID-236 — the part's curated, user-facing lifecycle activity feed,
   * newest-first. Sourced directly from the `GET /api/parts/:partId`
   * response's `activityLog` (normalized in `usePartsLibrary.ts`'s
   * `bffGetPartDetail`) — there is no separate activity endpoint/call. */
  readonly activity: readonly PartActivityEvent[]
  /** UID-332 (AC-B2) — the flat `revisions` grouped into revision→orders cards.
   * OPTIONAL/additive (mirrors the contract); the server always emits it (empty
   * for a part with no purchased revisions), the flat `revisions` above is
   * retained for backward-compat. */
  readonly revisionGroups?: readonly PartRevision[]
  /** UID-332 (AC-B3) — the per-part active-quote list. OPTIONAL/additive; the
   * server always emits it (empty when no quotes are joined) and
   * `activeQuoteCount` agrees by construction. */
  readonly activeQuotes?: readonly PartActiveQuote[]
  /** UID-332 (AC-B4) — the header's RFQ membership chips. OPTIONAL/additive
   * (empty when the part is in no RFQ); derive the "In RFQ" affordance from
   * `inRfq || (rfqChips?.length ?? 0) > 0`. */
  readonly rfqChips?: readonly PartRfqChip[]
  /** UID-332 (AC-B1) — the quoted-pricing card; present ONLY when there is a
   * quote to show (its presence is the `isQuote` signal). */
  readonly quotedPricing?: PartDetailQuotedPricing
  /** UID-332 (AC-B4) — free-text header description; `null`/absent when none. */
  readonly description?: string | null
}

export type PartActivityEventKind =
  | 'uploaded'
  | 'analyzed'
  | 'configured'
  | 'priced'
  | 'added_to_rfq'
  | 'ordered'

/** Who/what generated an activity entry (UID-236) — a badging discriminator
 * so the FE never has to parse `actor` to tell a customer action from a
 * Fictiv-side or system-generated one. */
export type PartActivityActorKind = 'customer' | 'fictiv' | 'system'

/** UID-236 — one curated, user-facing entry in a part's activity feed,
 * carried on `PartDetail.activityLog` (contract field name; the generated
 * schema's `message` is normalized to this view model's `label` in
 * `usePartsLibrary.ts`'s `bffGetPartDetail`, per this repo's "normalize
 * generated type into a feature view model" convention). */
export interface PartActivityEvent {
  readonly id: string
  readonly kind: PartActivityEventKind
  readonly label: string
  readonly occurredAt: string
  readonly actor?: string
  readonly actorKind?: PartActivityActorKind
}

/** Thrown/rejected by `getPartDetail` for an unknown or cross-tenant part
 * id — the contract's "tenant-scoped 404" — so the screen can render a
 * "Part not found" empty state rather than the generic error state. */
export class PartNotFoundError extends Error {
  constructor(partId: string) {
    super(`Part "${partId}" was not found`)
    this.name = 'PartNotFoundError'
  }
}

/**
 * UID-47 BOM detail (screens.md §7) — `GET /api/boms/:bomId` contract
 * handoff: a BOM's header/stats plus a RECURSIVE tree of assemblies + leaf
 * parts. `sourceable` is only present on `kind: 'part'` leaves (assemblies
 * are structural groupings, not Sourceables themselves).
 */
export type BomLineItemKind = 'assembly' | 'part'

export interface BomLineItem {
  readonly lineId: string
  readonly kind: BomLineItemKind
  readonly sourceable?: LibrarySourceable
  readonly name: string
  readonly revision?: string
  readonly quantity: number
  /** UID-295/296 — richer per-row display attributes for the redesigned detail
   * table. Leaf parts surface `mpn`/`category`/`leadTime`/`datasheetUrl`/
   * `thumbnailUrl` (joined BFF-side from the Library) plus the line-scoped
   * `descriptionLink`; assemblies/unenriched rows leave them null/absent. */
  readonly mpn?: string | null
  readonly category?: string | null
  readonly leadTime?: string | null
  readonly datasheetUrl?: string | null
  readonly thumbnailUrl?: string | null
  readonly descriptionLink?: string | null
  readonly children?: readonly BomLineItem[]
}

export interface BomStatCards {
  readonly totalParts: number
  readonly estimatedCost: Price | null
  readonly maxLeadTimeDays: number | null
  /** UID-295/296 — count of UNIQUE leaf sourceables for the "N unique" caption
   * (distinct from `totalParts`). Absent when the BFF did not send it. */
  readonly distinctPartCount?: number
}

export interface BomDetail {
  readonly id: string
  readonly name: string
  readonly status: BomLibraryStatus
  readonly stats: BomStatCards
  readonly lines: readonly BomLineItem[]
  readonly updatedAt: string
  /** UID-295/296 — free-form BOM-level description shown in the detail header. */
  readonly description?: string | null
}

/** UID-296 — the result of creating a "Share a BOM" link (`POST /boms/:bomId/share`).
 * The raw `token` is surfaced ONCE for the UI to show/copy and can never be
 * re-fetched (only its hash is persisted server-side). */
export interface BomShareResult {
  readonly shareId: string
  readonly token: string
  readonly expiresAt: string | null
}

/** UID-326 (ADR-0108) — one BOM-linked line of a placed order, projected for the
 * Order History card's "Parts ordered" row. `lineSubtotal` is SERVER-DERIVED
 * (unitPrice × quantity) — this view model never re-derives it. `quantity` is
 * normalized to the plain multiplier the card renders ("{n}×"); the wire
 * `Quantity.uom` isn't surfaced here (no design need for it on this card). */
export interface BomOrderCardLine {
  readonly name: string
  readonly partNumber: string | null
  readonly quantity: number
  readonly unitPrice: Price | null
  readonly lineSubtotal: Price
  readonly thumbnailUrl: string | null
}

/** UID-326 (ADR-0108) — one order placed against a BOM, rendered as one
 * card in the Order History tab (`GET /boms/:bomId/orders`), newest-first.
 * `total` is a SERVER-DERIVED subset merchandise subtotal of ONLY this BOM's
 * linked lines — labeled "Subtotal" in the UI, NEVER "Order total" (it is
 * not the whole order's charged total). `deliveredOn`/tracking are NOT
 * separate fields — derive them FE-side from `shipments` (reusing the orders
 * vertical's shipment-status presentation, `verticals/orders/domain.ts`).
 * `orderedBy` is a display name — treat as PII-adjacent. */
export interface BomOrderCard {
  readonly orderId: string
  readonly poReference: string | null
  readonly status: OrderStatus
  readonly orderedOn: string
  readonly orderedBy: string
  readonly total: Price
  readonly lines: readonly BomOrderCardLine[]
  readonly shipments: readonly Shipment[]
}

/** `GET /api/boms/add-parts/search` result row — spans both the parts
 * library and the standard catalog (components.md "Assembly tree (BOM)" /
 * screens.md §7's add-parts modal). */
export interface AddPartsSearchResult {
  readonly id: string
  readonly name: string
  readonly source: 'library' | 'catalog'
  readonly price: Price | null
}

/** Thrown/rejected by `getBomDetail` for an unknown or cross-tenant BOM id
 * (mirrors `PartNotFoundError`'s tenant-scoped-404 pattern). */
export class BomNotFoundError extends Error {
  constructor(bomId: string) {
    super(`BOM "${bomId}" was not found`)
    this.name = 'BomNotFoundError'
  }
}
